import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

function Sticky({
  sides, offerTableRef = {}, scrollTarget, children,
}) {
  const baseClass = 'mn_sticky';

  const [stickyState, setStickyState] = useState({
    height: 0,
    width: 0,
    stuckBottom: false,
    stuckLeft: false,
    stuckRight: false,
    stuckTop: false,
  });

  const stickyDiv = useRef();

  let frameId = 0;

  const handleScroll = () => {
    const target = scrollTarget || window;

    frameId = 0;
    if (!stickyDiv.current) {
      return;
    }

    // Prevent sticky behavior if Offer Table is almost scrolled out from viewport
    if (offerTableRef.current && offerTableRef.current.getBoundingClientRect().bottom - 300 < 0) {
      setStickyState({
        ...stickyState,
        stuckTop: false,
      });

      return;
    }

    const scrollRect = target.getBoundingClientRect
      ? target.getBoundingClientRect()
      : { // scrollTarget is the window
        height: target.innerHeight,
        width: target.innerWidth,
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        x: target.scrollX,
        y: target.scrollY,
      };

    let stickyRect = stickyDiv.current.getBoundingClientRect();

    if (!stickyState.height || !stickyState.width) {
      setStickyState({
        ...stickyState,
        height: stickyRect.height,
        width: stickyRect.height,
      });
    }

    stickyRect = { // Apparently you can't spread the results of a bounding client rectangle
      height: stickyState.height || stickyRect.height,
      width: stickyState.width || stickyRect.width,
      x: stickyRect.x,
      y: stickyRect.y,
    };

    if (typeof sides.bottom === 'number') {
      const stuckBottom = stickyRect.y + stickyRect.height > (scrollRect.height + scrollRect.top) - sides.bottom;
      setStickyState({
        ...stickyState,
        stuckBottom,
      });
    }

    if (typeof sides.top === 'number') {
      const stuckTop = stickyRect.y < scrollRect.top + sides.top;
      setStickyState({
        ...stickyState,
        stuckTop,
      });
    }

    if (typeof sides.left === 'number') {
      const stuckLeft = stickyRect.x < scrollRect.left + sides.left;
      setStickyState({
        ...stickyState,
        stuckLeft,
      });
    }

    if (typeof sides.right === 'number') {
      const stuckRight = stickyRect.x + stickyRect.width > (scrollRect.width + scrollRect.left) - sides.right;
      setStickyState({
        ...stickyState,
        stuckRight,
      });
    }
  };

  const debouncedScroll = () => {
    if (!frameId) {
      frameId = requestAnimationFrame(handleScroll);
    }
  };

  const addEvents = () => {
    const target = scrollTarget || window;

    if (target && stickyDiv.current) {
      target.addEventListener('scroll', debouncedScroll);
    }
  };

  const removeEvents = () => {
    const target = scrollTarget || window;

    if (target) {
      target.removeEventListener('scroll', debouncedScroll);
    }

    if (frameId) {
      cancelAnimationFrame(frameId);
    }
  };

  useEffect(() => {
    addEvents();

    return () => {
      removeEvents();
    };
  }, []);

  useEffect(() => {
    handleScroll();
  }, []);

  useEffect(() => {
    removeEvents();
    addEvents();
  }, [scrollTarget]);

  const {
    stuckBottom, stuckLeft, stuckRight, stuckTop,
  } = stickyState;

  const stickyModifiers = [];

  if (stuckBottom) {
    stickyModifiers.push('stuck_bottom');
  }

  if (stuckLeft) {
    stickyModifiers.push('stuck_left');
  }

  if (stuckRight) {
    stickyModifiers.push('stuck_right');
  }

  if (stuckTop) {
    stickyModifiers.push('stuck_top');
  }

  const childrenWithStuckProps = React.Children.map(children, (child) => {
    const childModifiers = (child.props && child.props.modifiers) || [];
    return React.cloneElement(child, { modifiers: [...childModifiers, ...stickyModifiers] });
  });

  return (
    <div
      className={baseClass}
      ref={stickyDiv}
    >
      {childrenWithStuckProps}
    </div>
  );
}

Sticky.propTypes = {
  /** Pass in a React component, and it will receive `stuckBottom`,
   * `stuckLeft`, `stuckRight`, and/or `stuckTop` modifiers */
  children: PropTypes.node.isRequired,
  /** If you have an internally scrolling component, pass its ref callback to watch for scroll events */
  scrollTarget: PropTypes.shape({}),
  /** These offsets determine how far from the edge of the page an element must be to count as 'stuck' */
  sides: PropTypes.shape({
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
    top: PropTypes.number,
  }),
  offerTableRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

Sticky.defaultProps = {
  scrollTarget: null,
  sides: {
    top: 0,
  },
  offerTableRef: null,
};

export default Sticky;
